import { useState, useEffect, useCallback, useRef } from 'react';
import { User } from '../types/profile';
import { userService } from '../services/user'; // Pour les mocks
import userApiService from '../services/userApi'; // Pour l'API réelle
import { getToken } from '../utils/storage';
import { useToast } from './useToast';
import { checkBackendHealth, getBackendHealthStatus } from '../utils/backendHealth';
import { ensureFreshToken } from '../utils/tokenRefresh';

// Constantes pour les tentatives de récupération
const MAX_RETRIES = 2;
const RETRY_DELAY = 3000; // 3 secondes

// Créer un événement personnalisé pour notifier les changements d'authentification
export const AUTH_EVENT = 'auth_state_changed';

// Fonction utilitaire pour déclencher l'événement d'authentification
export function notifyAuthChange() {
  const event = new CustomEvent(AUTH_EVENT);
  window.dispatchEvent(event);
}

interface UseUserOptions {
  preferMock?: boolean;
}

export function useUser(options: UseUserOptions = { preferMock: false }) {
  const [user, setUser] = useState<User | null>(null);
  const [isEnrichingData, setIsEnrichingData] = useState<boolean>(false);
  const [isUpdating, setIsUpdating] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);
  const [usingMockData, setUsingMockData] = useState<boolean>(options.preferMock || false);
  const userInitialized = useRef(false);
  const retryCountRef = useRef(0); // Utiliser ref pour éviter les dépendances circulaires
  
  const toast = useToast();

  // Fonction pour initialiser l'utilisateur avec les données Auth0
  const initializeUserFromAuth0 = useCallback(() => {
    const token = getToken();
    const auth0UserData = localStorage.getItem('auth0_user');
    
    console.log('🔍 initializeUserFromAuth0 - État:', {
      hasToken: !!token,
      hasAuth0Data: !!auth0UserData,
      tokenLength: token?.length || 0
    });
    
    const isTokenValid = !!token;
    setIsAuthenticated(isTokenValid);
    
    // Initialiser immédiatement avec les données Auth0 si disponibles
    if (auth0UserData && isTokenValid) {
      try {
        const auth0User = JSON.parse(auth0UserData);
        // Vérifier que les champs essentiels sont présents
        const userWithDefaults = {
          ...auth0User,
          // Mapper l'identifiant Auth0 vers notre champ id
          id: auth0User.sub || auth0User.id || '',
          // Garantir que ces champs existent toujours
          name: auth0User.name || auth0User.nickname || auth0User.email || 'Utilisateur',
          email: auth0User.email || 'N/A',
          picture: auth0User.picture || `https://ui-avatars.com/api/?name=${encodeURIComponent(auth0User.name || auth0User.email || 'U')}`,
        };
        setUser(userWithDefaults);
        console.log('👤 Utilisateur Auth0 initialisé:', userWithDefaults.name || userWithDefaults.email);
        return userWithDefaults;
      } catch (e) {
        console.error('❌ Erreur lors de la lecture des données Auth0 du localStorage', e);
        // Nettoyer les données corrompues
        localStorage.removeItem('auth0_user');
        setUser(null);
        setIsAuthenticated(false);
        return null;
      }
    }
    
    // Si pas de token valide, nettoyer l'état utilisateur
    if (!isTokenValid) {
      setUser(null);
      setError(null);
      retryCountRef.current = 0;
    }
    
    return null;
  }, []);

  // Fonction pour enrichir les données utilisateur à partir de l'API ou des mocks
  const enrichUserData = useCallback(async (initialUser: User | null) => {
    if (!initialUser) {
      console.log('❌ Pas d\'utilisateur initial pour enrichir les données');
      return;
    }
    
    // Vérifier le token plutôt que isAuthenticated pour plus de fiabilité
    const token = getToken();
    if (!token) {
      console.log('❌ Pas de token disponible pour enrichir les données');
      return;
    }
    
    console.log('🚀 Enrichissement des données utilisateur démarré pour:', initialUser.name || initialUser.email);
    
    // Ne déclenche l'enrichissement que si l'utilisateur est authentifié
    setIsEnrichingData(true);
    
    try {
      let enrichedUser: User | null = null;
      
      if (!options.preferMock) {
        // Vérifier d'abord la santé du backend
        const isBackendHealthy = await checkBackendHealth();
        
        if (isBackendHealthy) {
          try {
            // DEBUG: Vérifier l'état de l'authentification avant la requête
            console.log('🔍 DEBUG - État auth avant requête API:');
            console.log('- Token présent:', !!token);
            console.log('- Token longueur:', token?.length || 0);
            console.log('- URL Backend:', import.meta.env.VITE_API_URL);
            console.log('- Utilisateur initial:', initialUser.name || initialUser.email);
            // Debug simplifié
            console.log('🔍 État d\'authentification vérifié');
            
            // Essayer d'abord d'obtenir les données depuis l'API
            console.log('🌐 Récupération des données utilisateur depuis l\'API');
            const apiUser = await userApiService.getCurrentUser(true); // forceRefresh=true
            
            // Fusion des données Auth0 et des données de l'API
            enrichedUser = {
              ...initialUser,
              ...apiUser,
              // S'assurer que certains champs Auth0 sont préservés
              picture: apiUser.picture || initialUser.picture,
            };
            
            // Profil utilisateur préparé
            try {
              // Re-récupérer pour s'assurer de la cohérence
              const refreshedUser = await userApiService.getCurrentUser(true);
              enrichedUser = {
                ...enrichedUser,
                ...refreshedUser,
              };
            } catch (fixError) {
              console.warn('⚠️ Correction automatique échouée:', fixError);
            }
            
            setUsingMockData(false);
            console.log('✅ Données utilisateur récupérées depuis l\'API avec succès');
          } catch (apiError: any) {
            console.log('❌ Erreur lors de la récupération utilisateur:', apiError);
            
            // Vérifier si c'est une erreur 401 (pas d'auth) ou 404 (utilisateur n'existe pas)
            if (apiError?.status === 401) {
              console.log('🔑 Erreur 401 - Token invalide ou manquant');
              
              // Vérifier si c'est un premier signup (token pourrait être valide mais user pas créé)
              const isFirstTime = sessionStorage.getItem('auth0_first_time_user') === 'true';
              
              if (isFirstTime) {
                console.log('👤 Premier signup détecté malgré 401, tentative de création...');
                try {
                  // Forcer un refresh du token avant de créer l'utilisateur
                  const freshToken = await ensureFreshToken();
                  if (freshToken) {
                    console.log('🔄 Token rafraîchi, nouvelle tentative de création...');
                    
                    const newUser = await userApiService.createFirstTimeUser({
                      email: initialUser.email,
                      name: initialUser.name || initialUser.email || 'Utilisateur',
                      userType: 'sme',
                    });
                    
                    enrichedUser = {
                      ...initialUser,
                      ...newUser,
                      picture: newUser.picture || initialUser.picture,
                    };
                    
                    setUsingMockData(false);
                    console.log('✅ Premier utilisateur créé avec succès après refresh token');
                    sessionStorage.removeItem('auth0_first_time_user');
                  } else {
                    throw new Error('Impossible de rafraîchir le token');
                  }
                } catch (createError) {
                  console.warn(`⚠️ Échec création après 401, fallback mocks: ${createError instanceof Error ? createError.message : 'Erreur inconnue'}`);
                  const mockUser = await userService.getProfile(true);
                  enrichedUser = { ...initialUser, ...mockUser };
                  setUsingMockData(true);
                }
              } else {
                console.warn('⚠️ Erreur 401 pour utilisateur existant, fallback mocks');
                const mockUser = await userService.getProfile(true);
                enrichedUser = { ...initialUser, ...mockUser };
                setUsingMockData(true);
              }
            } else if (apiError?.status === 404) {
              console.log('👤 Utilisateur non trouvé côté backend (404), création du premier utilisateur...');
              
              try {
                // Vérifier si c'est vraiment un premier utilisateur
                const isFirstTime = sessionStorage.getItem('auth0_first_time_user') === 'true';
                console.log(`📝 Création utilisateur backend (premier utilisateur: ${isFirstTime})`);
                
                // Créer l'utilisateur côté backend pour la première fois
                const newUser = await userApiService.createFirstTimeUser({
                  email: initialUser.email,
                  name: initialUser.name || initialUser.email || 'Utilisateur',
                  userType: 'sme', // Par défaut, peut être modifié après
                });
                
                // Fusion avec les données Auth0
                enrichedUser = {
                  ...initialUser,
                  ...newUser,
                  picture: newUser.picture || initialUser.picture,
                };
                
                setUsingMockData(false);
                console.log('✅ Premier utilisateur créé avec succès côté backend');
                
                // Nettoyer le flag après création
                sessionStorage.removeItem('auth0_first_time_user');
              } catch (createError) {
                console.warn(`⚠️ Erreur lors de la création du premier utilisateur, utilisation des données mockées: ${createError instanceof Error ? createError.message : 'Erreur inconnue'}`);
                // En cas d'échec de création, fallback sur les données mockées
                const mockUser = await userService.getProfile(true);
                enrichedUser = { ...initialUser, ...mockUser };
                setUsingMockData(true);
              }
            } else {
              console.warn(`⚠️ Erreur API (${apiError?.status || 'inconnue'}), utilisation des données mockées: ${apiError instanceof Error ? apiError.message : 'Erreur inconnue'}`);
              // En cas d'autre erreur API, fallback sur les données mockées
              const mockUser = await userService.getProfile(true);
              enrichedUser = { ...initialUser, ...mockUser };
              setUsingMockData(true);
            }
          }
        } else {
          console.warn('⚠️ Backend non disponible, utilisation des données mockées');
          // Backend non disponible, utiliser directement les mocks
          const mockUser = await userService.getProfile(true);
          enrichedUser = { ...initialUser, ...mockUser };
          setUsingMockData(true);
        }
      } else {
        // Utilisation directe des données mockées si préférées
        console.log('🧪 Utilisation préférée des données mockées pour l\'utilisateur');
        const mockUser = await userService.getProfile(true); // forceRefresh=true
        enrichedUser = { ...initialUser, ...mockUser };
        setUsingMockData(true);
      }
      
      if (enrichedUser) {
        console.log('✨ Données utilisateur enrichies:', enrichedUser.name);
        setUser(enrichedUser);
      }
      
      setError(null);
      retryCountRef.current = 0; // Reset du compteur de retry
    } catch (err) {
      const error = err as Error;
      console.error('❌ Erreur lors de l\'enrichissement des données utilisateur:', error);
      
      setError(error.message);
      
      // Système de retry si nécessaire SEULEMENT si le backend était censé être disponible
      const backendStatus = getBackendHealthStatus();
      if (backendStatus === 'healthy' && retryCountRef.current < MAX_RETRIES) {
        const nextRetry = retryCountRef.current + 1;
        console.log(`⏱️ Tentative de récupération ${nextRetry}/${MAX_RETRIES} dans ${RETRY_DELAY/1000}s...`);
        retryCountRef.current = nextRetry;
        
        setTimeout(() => {
          enrichUserData(initialUser);
        }, RETRY_DELAY);
      } else {
        console.error('❌ Nombre maximum de tentatives atteint ou backend indisponible');
        // Fallback final sur les données mockées en cas d'échec total
        try {
          const mockUser = await userService.getProfile(true);
          const fallbackUser = { ...initialUser, ...mockUser };
          setUser(fallbackUser);
          setUsingMockData(true);
          console.log('🧪 Fallback final sur les données mockées');
        } catch (mockError) {
          console.error('❌ Échec du fallback mockées également:', mockError);
        }
      }
    } finally {
      setIsEnrichingData(false);
    }
  }, [options.preferMock]); // Supprimer isAuthenticated des dépendances
  
  // Initialisation des données utilisateur au démarrage
  useEffect(() => {
    console.log('🚀 useUser - Initialisation au démarrage...');
    const userData = initializeUserFromAuth0();
    if (userData) {
      console.log('👤 Utilisateur initialisé avec données Auth0:', userData.name);
      userInitialized.current = true;
      
      // Enrichir les données après l'initialisation
      enrichUserData(userData);
    } else {
      console.log('❌ Aucune donnée Auth0 trouvée au démarrage');
    }
  }, []); // Enlever les dépendances pour éviter les boucles - exécution une seule fois

  // Écouter les changements dans le localStorage pour les tokens Auth0
  useEffect(() => {
    const handleStorageChange = (e: StorageEvent) => {
      // Éviter les boucles - ne traiter que les changements externes
      if (e.storageArea === localStorage && (e.key === 'auth0_token' || e.key === 'auth0_user')) {
        console.log('🔄 Changement externe détecté dans le stockage Auth0, rechargement du profil');
        const userData = initializeUserFromAuth0();
        if (userData) {
          enrichUserData(userData);
        }
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, []); // Pas de dépendances - les fonctions sont stables

  // Écouter les changements d'authentification
  useEffect(() => {
    const handleAuthChange = () => {
      console.log("🔑 Changement d'état d'authentification détecté");
      const userData = initializeUserFromAuth0();
      if (userData) {
        enrichUserData(userData);
      }
    };

    window.addEventListener(AUTH_EVENT, handleAuthChange);
    return () => window.removeEventListener(AUTH_EVENT, handleAuthChange);
  }, []); // Pas de dépendances - fonctions stables
  
  // Fonction pour mettre à jour le profil utilisateur
  const updateProfile = async (userData: Partial<User>) => {
    if (!user) return null;
    
    setIsUpdating(true);
    
    try {
      let updatedUser: User;
      
      try {
        if (usingMockData) {
          throw new Error('Utilisant des données mockées, continuant avec le mock');
        }
        
        // Essayer d'abord l'API
        console.log('📤 Envoi des données utilisateur à l\'API...');
        const apiUser = await userApiService.updateUserProfile({
          name: userData.name,
          phone: userData.phone,
          address: userData.address
        });
        
        // Fusion des données mises à jour avec les données utilisateur existantes
        updatedUser = {
          ...user,
          ...apiUser,
          picture: apiUser.picture || user.picture
        };
        
        console.log('✅ Profil utilisateur mis à jour via l\'API avec succès');
        
        // Mettre à jour également le mock pour garder la cohérence
        await userService.updateProfile(userData);
      } catch (apiError) {
        console.warn(`⚠️ Impossible de mettre à jour via l'API, utilisation du mock: ${apiError instanceof Error ? apiError.message : 'Erreur inconnue'}`);
        // En cas d'échec, mise à jour via le mock
        updatedUser = await userService.updateProfile(userData);
        setUsingMockData(true);
      }
      
      setUser(updatedUser);
      setError(null);
      
      toast.showToast?.({
        type: 'success',
        title: 'Profil mis à jour',
        message: 'Vos informations ont été mises à jour avec succès'
      });
      
      return updatedUser;
    } catch (err) {
      const error = err as Error;
      console.error('❌ Erreur lors de la mise à jour du profil:', error);
      
      setError(error.message);
      
      toast.showToast?.({
        type: 'error',
        title: 'Erreur',
        message: `Échec de la mise à jour du profil: ${error.message}`
      });
      
      return null;
    } finally {
      setIsUpdating(false);
    }
  };
  
  // Fonction pour uploader une photo de profil
  const uploadProfilePhoto = async (file: File) => {
    if (!user) return null;
    
    setIsUpdating(true);
    
    try {
      let photoUrl: string;
      
      try {
        if (usingMockData) {
          throw new Error('Utilisant des données mockées, continuant avec le mock');
        }
        
        // Essayer d'abord l'API
        console.log('📤 Upload de la photo de profil via l\'API...');
        const result = await userApiService.uploadProfilePhoto(file);
        photoUrl = result.url;
        
        console.log('✅ Photo de profil uploadée via l\'API avec succès');
        
        // Mettre à jour également le mock pour garder la cohérence
        const mockResult = await userService.uploadProfilePhoto(file);
        photoUrl = mockResult.url;
      } catch (apiError) {
        console.warn(`⚠️ Impossible d'uploader via l'API, utilisation du mock: ${apiError instanceof Error ? apiError.message : 'Erreur inconnue'}`);
        // En cas d'échec, upload via le mock
        const result = await userService.uploadProfilePhoto(file);
        photoUrl = result.url;
        setUsingMockData(true);
      }
      
      // Mettre à jour l'utilisateur avec la nouvelle URL de photo
      setUser(prev => prev ? { ...prev, picture: photoUrl } : null);
      setError(null);
      
      toast.showToast?.({
        type: 'success',
        title: 'Photo mise à jour',
        message: 'Votre photo de profil a été mise à jour avec succès'
      });
      
      return photoUrl;
    } catch (err) {
      const error = err as Error;
      console.error('❌ Erreur lors de l\'upload de la photo:', error);
      
      setError(error.message);
      
      toast.showToast?.({
        type: 'error',
        title: 'Erreur',
        message: `Échec de l'upload de la photo: ${error.message}`
      });
      
      return null;
    } finally {
      setIsUpdating(false);
    }
  };

  // Fonction pour rafraîchir manuellement les données utilisateur
  const refreshUserData = () => {
    if (user) {
      enrichUserData(user);
    } else {
      const userData = initializeUserFromAuth0();
      if (userData) {
        enrichUserData(userData);
      }
    }
  };

  // Fonction pour synchroniser après login (utilisée par le callback Auth0)
  const syncProfileAfterLogin = useCallback(async () => {
    console.log('🔄 Synchronisation du profil après connexion Auth0');
    const userData = initializeUserFromAuth0();
    if (userData) {
      await enrichUserData(userData);
      // Pas besoin de notifyAuthChange car enrichUserData met déjà à jour l'état
    }
  }, [initializeUserFromAuth0, enrichUserData]);

  return {
    user,
    isAuthenticated,
    isLoading: isEnrichingData,
    isUpdating,
    error,
    usingMockData,
    updateProfile,
    uploadProfilePhoto,
    refreshUserData,
    syncProfileAfterLogin
  };
}
